import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { FeelLogger, type Feel } from "../components/FeelLogger";
import type {
  DeloadSignal,
  PrescribedSession,
  SessionWithSets,
  SetInput,
  TodayTraining,
} from "@hh/shared";

type View = "today" | "program" | "history";
type SetEntry = { weight: string; reps: string };

const emptyFeel = (): Feel => ({ energy: null, mood: null, flags: {} });

export function Train({ reloadKey, onLogged }: { reloadKey: number; onLogged: () => void }) {
  const [view, setView] = useState<View>("today");
  const [today, setToday] = useState<TodayTraining | null>(null);
  const [deload, setDeload] = useState<DeloadSignal | null>(null);
  const [toast, setToast] = useState("");

  // logging mode for the prescribed lift
  const [logging, setLogging] = useState<PrescribedSession | null>(null);
  const [sets, setSets] = useState<Record<number, SetEntry[]>>({});
  const [feel, setFeel] = useState<Feel>(emptyFeel());
  const [busy, setBusy] = useState(false);

  function reload() {
    api.trainToday().then(setToday).catch(() => {});
    api.deload().then(setDeload).catch(() => {});
  }
  useEffect(reload, [reloadKey]);

  function flash(msg: string) {
    setToast(msg); setTimeout(() => setToast(""), 1600); onLogged(); reload();
  }

  function startLift(session: PrescribedSession) {
    const init: Record<number, SetEntry[]> = {};
    for (const ex of session.exercises) {
      init[ex.exercise_id] = Array.from({ length: ex.sets }, () => ({
        weight: ex.suggested_weight != null ? String(ex.suggested_weight) : "",
        reps: ex.is_time_based ? String(ex.seconds ?? "") : String(ex.rep_low || ""),
      }));
    }
    setSets(init);
    setFeel(emptyFeel());
    setLogging(session);
    window.scrollTo(0, 0);
  }

  function updateSet(exId: number, i: number, patch: Partial<SetEntry>) {
    setSets((s) => ({ ...s, [exId]: s[exId].map((row, idx) => (idx === i ? { ...row, ...patch } : row)) }));
  }
  function addSet(exId: number) {
    setSets((s) => ({ ...s, [exId]: [...s[exId], { weight: s[exId].at(-1)?.weight ?? "", reps: "" }] }));
  }

  async function finishLift() {
    if (!logging) return;
    setBusy(true);
    try {
      const allSets: SetInput[] = [];
      for (const ex of logging.exercises) {
        (sets[ex.exercise_id] ?? []).forEach((row, idx) => {
          const reps = row.reps ? Number(row.reps) : null;
          const weight = row.weight ? Number(row.weight) : null;
          if (reps == null && weight == null) return; // skip blank
          allSets.push({ exercise_id: ex.exercise_id, set_no: idx + 1, weight, reps, rpe: null });
        });
      }
      await api.logSession({
        type: logging.kind,
        feel_energy: feel.energy,
        feel_mood: feel.mood,
        feel_flags: feel.flags,
        sets: allSets,
      });
      setLogging(null);
      flash("Session logged ✓");
    } finally {
      setBusy(false);
    }
  }

  async function quickLog(type: "walk" | "micro") {
    setBusy(true);
    try {
      await api.logSession(
        type === "walk"
          ? { type: "walk", distance_mi: 2.1, duration_min: 45 }
          : { type: "micro" },
      );
      flash(type === "walk" ? "Walk logged ✓" : "Micro done ✓");
    } finally { setBusy(false); }
  }

  // ----- logging mode -----
  if (logging) {
    return (
      <div class="app-shell fade-in">
        <div class="screen-head"><div><div class="eyebrow">{logging.focus}</div><h1>{logging.title}</h1></div></div>
        <div class="card">
          {logging.exercises.map((ex) => (
            <div class="ex-block" key={ex.exercise_id}>
              <div class="ex-head">
                <span class="ex-name">{ex.name}</span>
                <span class="ex-scheme">
                  {ex.is_time_based ? `${ex.sets}×${ex.seconds}s` : `${ex.sets}×${ex.rep_low}-${ex.rep_high}`}
                </span>
              </div>
              {ex.cues && <div class="ex-cue">{ex.cues}</div>}
              {(sets[ex.exercise_id] ?? []).map((row, i) => (
                <div class="set-row" key={i}>
                  <span class="sn">Set {i + 1}</span>
                  {ex.equipment !== "bodyweight" && !ex.is_time_based && (
                    <>
                      <input type="number" inputMode="decimal" placeholder="kg" value={row.weight}
                        onInput={(e) => updateSet(ex.exercise_id, i, { weight: (e.target as HTMLInputElement).value })} />
                      <span class="x">×</span>
                    </>
                  )}
                  <input type="number" inputMode="numeric" placeholder={ex.is_time_based ? "sec" : "reps"} value={row.reps}
                    onInput={(e) => updateSet(ex.exercise_id, i, { reps: (e.target as HTMLInputElement).value })} />
                  <span class="x">{ex.is_time_based ? "sec" : "reps"}</span>
                </div>
              ))}
              <button class="add-set" onClick={() => addSet(ex.exercise_id)}>+ add set</button>
            </div>
          ))}
        </div>

        <div class="card">
          <h2>How did it feel?</h2>
          <FeelLogger feel={feel} onChange={setFeel} />
        </div>

        <button class="btn" onClick={finishLift} disabled={busy}>{busy ? <span class="spinner" /> : "Log session"}</button>
        <button class="btn ghost" onClick={() => setLogging(null)} disabled={busy}>Cancel</button>
      </div>
    );
  }

  // ----- main -----
  return (
    <div class="app-shell fade-in">
      <div class="screen-head"><div><div class="eyebrow">Lifts · micro · cardio</div><h1>Train</h1></div></div>

      <div class="pill-row">
        {(["today", "program", "history"] as View[]).map((v) => (
          <button key={v} class={view === v ? "active" : ""} onClick={() => setView(v)}>
            {v[0].toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>

      {view === "today" && today && (
        <>
          {deload?.recommend && <div class="deload-banner">⚠️ {deload.reason}</div>}

          <div class="card quick-card">
            <div>
              <div style={{ fontWeight: 650 }}>🐕 Dog walk</div>
              <div class="muted">2.1 mi · ~45 min · the keystone</div>
            </div>
            {today.walkDone
              ? <span class="done" style={{ color: "var(--good)", fontWeight: 650 }}>Done ✓</span>
              : <button class="btn" style={{ width: "auto" }} onClick={() => quickLog("walk")} disabled={busy}>Log</button>}
          </div>

          {today.lift ? (
            <div class="card">
              <h2>{today.lift.title} — tonight</h2>
              <p class="muted" style={{ marginBottom: 12 }}>{today.lift.focus}</p>
              {today.lift.exercises.map((ex) => (
                <div class="ex-block" key={ex.exercise_id}>
                  <div class="ex-head">
                    <span class="ex-name">{ex.name}</span>
                    <span class="ex-scheme">
                      {ex.is_time_based ? `${ex.sets}×${ex.seconds}s` : `${ex.sets}×${ex.rep_low}-${ex.rep_high}`}
                      {ex.suggested_weight != null && ` · ${ex.suggested_weight}kg`}
                    </span>
                  </div>
                  {ex.cues && <div class="ex-cue">{ex.cues}</div>}
                </div>
              ))}
              {today.lift.note && <p class="muted" style={{ marginTop: 12 }}>{today.lift.note}</p>}
              <button class="btn" style={{ marginTop: 14 }} onClick={() => startLift(today.lift!)}>
                {today.liftLoggedToday ? "Log another session" : "Start session"}
              </button>
            </div>
          ) : (
            <div class="card">
              <div class="empty" style={{ padding: "20px" }}>
                <div class="ico">🌙</div>
                <p style={{ color: "var(--text-dim)", fontWeight: 600 }}>No lift scheduled today</p>
                <p>Rest day — the walk and a micro-session keep the streak alive.</p>
              </div>
            </div>
          )}

          <div class="card quick-card">
            <div>
              <div style={{ fontWeight: 650 }}>☀️ {today.micro.title}</div>
              <div class="muted">{today.micro.exercises.map((e) => e.name).join(" · ")}</div>
            </div>
            <button class="btn secondary" style={{ width: "auto" }} onClick={() => quickLog("micro")} disabled={busy}>Done</button>
          </div>
        </>
      )}

      {view === "program" && <ProgramView />}
      {view === "history" && <HistoryView reloadKey={reloadKey} onChange={reload} />}

      {toast && <div class="toast">{toast}</div>}
    </div>
  );
}

function ProgramView() {
  const [week, setWeek] = useState<{ day: string; session: PrescribedSession }[]>([]);
  useEffect(() => { api.trainProgram().then((r) => setWeek(r.week)).catch(() => {}); }, []);
  return (
    <>
      {week.map((d) => (
        <div class="card" key={d.day}>
          <h2>{d.day} — {d.session.title}</h2>
          {d.session.exercises.map((ex) => (
            <div class="ex-block" key={ex.exercise_id}>
              <div class="ex-head">
                <span class="ex-name">{ex.name}</span>
                <span class="ex-scheme">
                  {ex.is_time_based ? `${ex.sets}×${ex.seconds}s` : `${ex.sets}×${ex.rep_low}-${ex.rep_high}`}
                  {ex.suggested_weight != null && ` · ${ex.suggested_weight}kg`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function HistoryView({ reloadKey, onChange }: { reloadKey: number; onChange: () => void }) {
  const [sessions, setSessions] = useState<SessionWithSets[]>([]);
  function load() { api.trainSessions().then((r) => setSessions(r.sessions)).catch(() => {}); }
  useEffect(load, [reloadKey]);

  async function del(id: number) { await api.deleteSession(id); load(); onChange(); }

  if (!sessions.length) {
    return <div class="card"><div class="empty"><div class="ico">📋</div><p>No sessions logged yet.</p></div></div>;
  }
  return (
    <>
      {sessions.map((s) => (
        <div class="card" key={s.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <h2 style={{ marginBottom: 4, textTransform: "capitalize" }}>{s.type}</h2>
            <button class="del" onClick={() => del(s.id)} aria-label="Delete">×</button>
          </div>
          <div class="muted" style={{ marginBottom: 8 }}>
            {new Date(s.datetime).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            {s.distance_mi ? ` · ${s.distance_mi} mi` : ""}
            {s.feel_energy ? ` · energy ${s.feel_energy}/5` : ""}
          </div>
          {s.sets.map((set) => (
            <div class="diary-item" key={set.id}>
              <span>{set.exercise_name}</span>
              <span class="qty">{set.weight ? `${set.weight}kg × ` : ""}{set.reps ?? "—"}{set.weight ? "" : " reps"}</span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
